<template>
  <div class="page-wrapper">
    <div class="homepage">
      <div class="content">
        <h1>GPS 위성신호를 활용한<br />우주기상 탐사(sensing)</h1>
        <p class="subtitle">지도교수 방유진교수님</p>
        <div class="cta-buttons">
          <button class="btn primary" @click="goToExplore">Start Exploring</button>
          <button class="btn secondary" @click="toggleTeam">
            Team Member
          </button>
        </div>
      </div>
      <div class="glow-point"></div>
    </div>

    <!-- ✅ 조건부 렌더링된 Team Section -->
    <div
      class="team-section"
      v-if="showTeam"
      ref="teamSection"
    >
      <h2 class="team-title">Team Members</h2>
      <div class="team-grid">
        <div class="member-card" v-for="member in members" :key="member.name">
          <h3>{{ member.name }}</h3>
          <p v-html="member.task"></p>
        </div>
      </div>
    </div>
  </div>
</template>


<script setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'

const router = useRouter()

const showTeam = ref(false)
const teamSection = ref(null)

const toggleTeam = () => {
  showTeam.value = !showTeam.value
  if (showTeam.value) {
    // 보일 때 스크롤 이동
    requestAnimationFrame(() => {
      teamSection.value?.scrollIntoView({ behavior: 'smooth' })
    })
  }
}

const members = [
  { name: '장창수(팀장)', task: '데이터 전처리' },
  { name: '배서영(머신러닝 팀)', task: '머신러닝 데이터 전처리<br /> 넬더 미드 기법의 최적화 데이터 처리<br />머신러닝 모델 제작' },
  { name: '권지원(머신러닝 팀)', task: '머신러닝 데이터 전처리<br /> 넬더 미드 기법의 최적화 데이터 처리<br />머신러닝 모델 제작' },
  { name: '박지연(주파수 팀)', task: 'GPS 위성 위치 추출 <br /> TEC 계산을 위한 코드 구현(수신기)' },
  { name: '김예진(주파수 팀)', task: 'TEC 계산을 위한 코드 구현(수신기)' }
]

const goToExplore = () => {
  router.push('/explore')
}

</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;600;700&display=swap');

/* 전체 화면 초기화 */
html, body {
  margin: 0;
  padding: 0;
  font-family: 'Outfit', sans-serif;
}

/* ✅ Home 영역 */
.homepage {
  width: 100vw;
  height: 100vh;
  background:
    radial-gradient(circle at 25% 35%, rgba(90, 120, 255, 0.1), transparent 60%),
    radial-gradient(circle at 75% 75%, rgba(0, 200, 255, 0.15), transparent 70%),
    linear-gradient(135deg, #0b0c2a, #13143d, #0b0c2a);
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding-left: 8vw;
  position: relative;
  color: white;
  overflow: hidden;
}

.content {
  z-index: 2;
  margin-left: 2rem;
}

h1 {
  font-size: 3rem;
  font-weight: 700;
  line-height: 1.3;
  margin-bottom: 1rem;
}

.subtitle {
  font-size: 1.2rem;
  color: #cfd3f5;
  margin-bottom: 2rem;
}

.cta-buttons {
  display: flex;
  gap: 1rem;
}

.btn {
  padding: 0.8rem 1.6rem;
  border-radius: 10px;
  font-weight: 600;
  font-size: 1rem;
  text-decoration: none;
  transition: all 0.3s ease;
  border: 1px solid #4267ff;
  background: transparent;
  cursor: pointer;
}

.btn.primary {
  background: #4267ff;
  color: white;
}

.btn.secondary {
  color: #aab4ff;
}

.btn:hover {
  background: #2e4dff;
  color: #ffffff;
}

/* ✅ glow point */
.glow-point {
  position: absolute;
  bottom: 15%;
  right: 15%;
  width: 350px;
  height: 350px;
  background: radial-gradient(circle, rgba(150, 180, 255, 0.25) 0%, transparent 70%);
  border-radius: 50%;
  filter: blur(60px);
  animation: pulse 6s infinite ease-in-out;
  z-index: 1;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 0.4;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.8;
  }
}

/* ✅ Team Section */
.team-section {
  padding: 5rem 2rem 6rem;
  text-align: center;
  background: linear-gradient(to bottom, #0b0d1a, #111223);
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  min-height: 100vh;
}

.team-title {
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 2rem;
  color: #e0e4ff;
}

.team-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 2rem;
  justify-items: center;
}

.member-card {
  background: linear-gradient(145deg, rgba(31, 92, 255, 0.05), rgba(255, 255, 255, 0.02));
  border: 1px solid rgba(120, 150, 255, 0.25);
  border-radius: 16px;
  padding: 1.5rem;
  width: 100%;
  max-width: 260px;
  box-shadow: 0 0 10px rgba(120, 150, 255, 0.15);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  color: #dce0ff;
}

.member-card h3 {
  margin-bottom: 0.8rem;
  font-size: 1.3rem;
  font-weight: 600;
  color: #ffffff;
}

.member-card p {
  font-size: 0.95rem;
  line-height: 1.5;
  color: #c3c8ff;
}

.member-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 0 18px rgba(100, 180, 255, 0.3);
}
</style>

<style>
/* 전역 여백 제거 */
html, body {
  margin: 0;
  padding: 0;
  width: 100%;
  height: 100%;
}
</style>
